<script setup>
/* La colonna di navigazione dell'archivio.
 *
 * Qui vive ciò che prima stava sparso: il passaggio al feed (era un tasto
 * nella barra in alto) e le board (erano una scheda della home). L'ordine è
 * quello d'uso: prima il feed, che è l'altra metà dell'app; poi l'elenco
 * delle board una per una, con il + per crearne una nuova senza passare dal
 * menu. Alla griglia con tutte si arriva dal marchio in alto: qui sarebbe
 * stata una voce in più per una destinazione che si raggiunge già.
 *
 * Fuori dall'app la voce Feed non esiste (nessuna finestra da mostrare), e
 * senza sessione non esistono board da elencare: se non resta niente da
 * mostrare, la colonna intera sparisce.
 *
 * Ogni voce è anche un bersaglio di rilascio: trascinandoci sopra immagini o
 * una cartella dal Finder si archivia in QUELLA board, senza doverla prima
 * aprire. È ciò che rende la colonna uno smistatoio e non solo un elenco di
 * collegamenti.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { Board, Pin } from '@/api';
import { errorMessage } from '@/api/client';
import BoardEditorModal from '@/components/BoardEditorModal.vue';
import { useBoardActions } from '@/composables/useBoardActions';
import {
  conFile,
  conImmagineWeb,
  conPin,
  leggiDrop,
  segnalaZonaPrecisa,
  TIPO_PIN,
  urlImmagine,
  useImportCartella,
} from '@/composables/useImportCartella';
import { useAuthStore } from '@/stores/auth';
import { useBoardsStore } from '@/stores/boards';
import { useModalStore } from '@/stores/modals';
import { useUiStore } from '@/stores/ui';

const auth = useAuthStore();
const boards = useBoardsStore();
const modals = useModalStore();
const ui = useUiStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { importa } = useImportCartella();
const azioniBoard = useBoardActions();

/* Il menu contestuale della voce. Le board della colonna sono per
 * definizione le proprie, quindi ci sono tutte le voci; e non serve dire cosa
 * fare dopo, perché ogni azione ricarica da sé questa lista (vedi
 * useBoardActions). Se la board aperta è quella eliminata si torna alla
 * griglia: restare su una pagina che non esiste più mostrerebbe un titolo
 * vuoto e una griglia in errore.
 *
 * La lista della colonna arriva dall'autocomplete, che è volutamente magro. Se
 * il server è più vecchio del campo `private` si chiede la board intera prima
 * di aprire — una sola richiesta, solo quando il menu serve davvero, invece di
 * quaranta al caricamento della pagina. Il punto in cui apparirà si prende
 * *prima* dell'attesa: dopo l'await l'evento non ha più coordinate valide.
 */
async function menuBoard(evento, board) {
  evento.preventDefault();
  const punto = { clientX: evento.clientX, clientY: evento.clientY, preventDefault() {} };
  const completa =
    typeof board.private === 'boolean' ? board : await Board.one(board.id).catch(() => board);

  azioniBoard.apriMenu(punto, completa, {
    mia: true,
    dopoElimina: () => {
      if (route.name === 'pins-by-board' && String(route.params.boardId) === String(board.id)) {
        router.push({ name: 'boards-by-user', params: { username: auth.username } });
      }
    },
  });
}

// Vedi SiteHeader: il canale Tauri esiste solo dentro il guscio dell'app.
const dentroApp = typeof window !== 'undefined' && Boolean(window.__TAURI_INTERNALS__);

/* Il filtro delle board.
 *
 * Filtra QUELLO CHE C'È, non interroga il server: l'elenco della colonna è
 * già completo (arriva da boards-auto-complete, non paginato), quindi una
 * chiamata di rete per ogni lettera darebbe le stesse righe più tardi.
 *
 * Sta in fondo alla colonna e non in cima perché con quaranta board si
 * scorre, e il pollice — o il puntatore che ha appena finito di scorrere —
 * è già lì. */
const filtro = ref('');

const boardFiltrate = computed(() => {
  const cerca = filtro.value.trim().toLowerCase();
  if (!cerca) return boards.items;
  return boards.items.filter((b) => b.name.toLowerCase().includes(cerca));
});

function mostraFeed() {
  try {
    window.__TAURI_INTERNALS__?.invoke('mostra_finestra', { quale: 'feed' });
  } catch {
    // Fuori dal guscio il comando non esiste: meglio un nulla di fatto.
  }
}

async function creaBoard() {
  const board = await modals.open(BoardEditorModal, { board: null });
  if (!board) return;
  await boards.reload();
  boards.segnalaMutazione('creata', board);
  // Si atterra sulla board appena creata: vuota, ma è lei il contesto in cui
  // si sta per lavorare.
  router.push({ name: 'pins-by-board', params: { boardId: board.id } });
}

/* --- Rilascio su una singola voce ------------------------------------- */

/* Una sola voce per volta può stare sotto il puntatore, quindi basta il suo
 * id: niente contatori. Le voci non hanno figli (solo testo), quindi
 * dragenter e dragleave arrivano puliti, senza il tremolio che costringe a
 * contare altrove. */
const sotto = ref(null);

/* Una voce accetta tre cose: un file/cartella dal Finder, un pin trascinato
 * dalla griglia (lo aggiunge alla board), un'immagine dal web (la scarica nella
 * board). Il feed di Pinterest ricade nel terzo caso: è web anch'esso. */
function accetta(evento) {
  return conFile(evento) || conPin(evento) || conImmagineWeb(evento);
}

function mira(board, evento) {
  if (!accetta(evento)) return;
  evento.preventDefault();
  /* Ferma la corsa verso il documento: là c'è la zona di pagina, che
   * altrimenti reclamerebbe lo stesso rilascio e riscriverebbe il cursore. */
  evento.stopPropagation();
  evento.dataTransfer.dropEffect = 'copy';
  if (sotto.value !== board.id) {
    sotto.value = board.id;
    segnalaZonaPrecisa(true);
  }
}

function lascia(board, evento) {
  if (!accetta(evento)) return;
  evento.stopPropagation();
  if (sotto.value !== board.id) return;
  sotto.value = null;
  segnalaZonaPrecisa(false);
}

function smetti() {
  sotto.value = null;
  segnalaZonaPrecisa(false);
}

/** Aggiunge un pin già esistente a una board. */
async function aggiungiPin(board, pinId) {
  try {
    await Board.addPins(board.id, [pinId]);
    boards.segnalaImport(board.id);
    ui.notify(t('addedToBoard', { board: board.name }));
  } catch (errore) {
    ui.notifyError(errorMessage(errore, t('importFailed')));
  }
}

/** Crea un pin da un URL immagine (il server la scarica) e lo mette in board. */
async function aggiungiDaUrl(board, url) {
  try {
    // Come la raccolta: si manda l'URL, i byte li scarica il server.
    const pin = await Pin.create({ url });
    await Board.addPins(board.id, [pin.id]);
    boards.segnalaImport(board.id);
    ui.notify(t('addedToBoard', { board: board.name }));
  } catch (errore) {
    ui.notifyError(errorMessage(errore, t('importFailed')));
  }
}

async function rilascia(board, evento) {
  if (!accetta(evento)) return;
  evento.preventDefault();
  evento.stopPropagation();
  smetti();

  // Un pin trascinato dalla griglia: si aggiunge alla board, non si ri-carica.
  if (conPin(evento)) {
    const pinId = Number(evento.dataTransfer.getData(TIPO_PIN));
    if (pinId) await aggiungiPin(board, pinId);
    return;
  }

  // File e cartelle dal Finder: import come prima.
  if (conFile(evento)) {
    const { file, cartelle } = await leggiDrop(evento.dataTransfer);
    // Su una board precisa la destinazione è già decisa: file sciolti e
    // contenuto delle cartelle finiscono tutti lì dentro. La cartella qui non
    // dà il nome a niente.
    const tutte = [...file, ...cartelle.flatMap((c) => c.file)];
    if (!tutte.length) {
      ui.notify(t('dropNoImages'));
      return;
    }
    const esito = await importa({ boardId: board.id, nome: board.name, file: tutte });
    // Chi sta guardando questa board altrove aggiorna la propria griglia.
    if (esito) boards.segnalaImport(board.id);
    return;
  }

  // Un'immagine dal web (altra scheda, o il feed Pinterest).
  const url = urlImmagine(evento.dataTransfer);
  if (url) await aggiungiDaUrl(board, url);
  else ui.notify(t('dropNoImages'));
}
</script>

<template>
  <aside v-if="dentroApp || auth.isAuthenticated" class="fianco">
    <nav class="colonna" :aria-label="$t('archiveViewButton')">
      <!-- Le due destinazioni intere: l'altra finestra e la griglia con tutte
           le board. Stanno insieme in cima, sopra l'elenco una per una. -->
      <div v-if="dentroApp || auth.isAuthenticated" class="destinazioni">
        <button v-if="dentroApp" class="grande" type="button" @click="mostraFeed">
          <!-- Il glifo è la masonry stessa: due colonne a tessere sfalsate. -->
          <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
            <rect x="2.5" y="2.5" width="6.5" height="9" rx="1.6" fill="currentColor" />
            <rect x="2.5" y="13.5" width="6.5" height="4" rx="1.3" fill="currentColor" />
            <rect x="11" y="2.5" width="6.5" height="5" rx="1.3" fill="currentColor" />
            <rect x="11" y="9.5" width="6.5" height="8" rx="1.6" fill="currentColor" />
          </svg>
          {{ $t('feedViewButton') }}
        </button>

        <RouterLink
          v-if="auth.isAuthenticated"
          class="grande"
          :to="{ name: 'boards-by-user', params: { username: auth.username } }"
        >
          <!-- Quattro tessere pari e vuote: la griglia regolare delle board.
               Vuote e non piene perché accanto alla masonry del feed, a
               tessere piene, due glifi pieni si somigliavano troppo. -->
          <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
            <rect x="3.2" y="3.2" width="5.6" height="5.6" rx="1.4" fill="none" stroke="currentColor" stroke-width="1.6" />
            <rect x="11.2" y="3.2" width="5.6" height="5.6" rx="1.4" fill="none" stroke="currentColor" stroke-width="1.6" />
            <rect x="3.2" y="11.2" width="5.6" height="5.6" rx="1.4" fill="none" stroke="currentColor" stroke-width="1.6" />
            <rect x="11.2" y="11.2" width="5.6" height="5.6" rx="1.4" fill="none" stroke="currentColor" stroke-width="1.6" />
          </svg>
          {{ $t('myBoardsLink') }}
        </RouterLink>
      </div>

      <template v-if="auth.isAuthenticated">
        <div class="testata">
          <span class="label">{{ $t('boardsLink') }}</span>
          <button
            class="piu"
            type="button"
            :aria-label="$t('newBoardButton')"
            @click="creaBoard"
          >
            <svg viewBox="0 0 14 14" width="12" height="12" aria-hidden="true">
              <path d="M7 2v10M2 7h10" stroke="currentColor" stroke-width="1.8" fill="none" />
            </svg>
          </button>
        </div>

        <ul>
          <li v-for="board in boardFiltrate" :key="board.id">
            <RouterLink
              class="voce"
              :class="{
                'voce--attiva':
                  route.name === 'pins-by-board' && String(route.params.boardId) === String(board.id),
                'voce--mira': sotto === board.id,
              }"
              :to="{ name: 'pins-by-board', params: { boardId: board.id } }"
              @dragenter="mira(board, $event)"
              @dragover="mira(board, $event)"
              @dragleave="lascia(board, $event)"
              @dragend="smetti"
              @drop="rilascia(board, $event)"
              @contextmenu="menuBoard($event, board)"
            >
              <span class="voce__nome">{{ board.name }}</span>
              <!-- Il chiavistello: una board privata si riconosce nell'elenco
                   senza aprirla, ed è anche il riscontro visivo che il menu
                   ha fatto quello che ha detto. -->
              <svg
                v-if="board.private"
                class="voce__lucchetto"
                viewBox="0 0 16 16"
                width="11"
                height="11"
                aria-hidden="true"
              >
                <path
                  d="M4.5 7V5.2a3.5 3.5 0 0 1 7 0V7M3.6 7h8.8v6H3.6z"
                  stroke="currentColor"
                  stroke-width="1.4"
                  fill="none"
                />
              </svg>
            </RouterLink>
          </li>
        </ul>
      </template>
    </nav>

    <!-- Fuori dalla colonna che scorre, così resta in fondo e sempre a vista
         anche dopo aver scorso quaranta board. -->
    <div v-if="auth.isAuthenticated" class="filtro">
      <input
        v-model="filtro"
        class="filtro__campo"
        type="search"
        :placeholder="$t('searchBoardPlaceholder')"
        :aria-label="$t('searchBoardPlaceholder')"
      />
    </div>
  </aside>
</template>

<style scoped>
/* A differenza della barra in alto — cornice della finestra, colori fissi —
 * la colonna è contenuto della pagina: segue il tema come tutto il resto. */
.fianco {
  position: sticky;
  /* Sopra il velo del trascinamento (39): le board qui dentro sono bersagli,
     e vanno viste nitide proprio mentre lo si sta scegliendo. */
  z-index: 41;
  /* Sotto la barra: 52px è la misura del contratto col guscio, vedi
   * SiteHeader. */
  top: 52px;
  /* In rem e non in px: se il testo cresce (zoom del testo, corpo di sistema
     più grande) la colonna cresce con lui, invece di restare larga uguale e
     mangiarsi le parole. 14.5rem sono i 232px di prima al corpo normale. */
  flex: 0 0 14.5rem;
  height: calc(100vh - 52px);
  /* Lo scorrimento è passato alla colonna qui dentro: se restasse su .fianco,
     il filtro in fondo scorrerebbe via insieme alle board invece di stare
     fermo dove il dito lo lascia. */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-right: 1px solid var(--rule);
  background: var(--bg);
}

.colonna {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: var(--s-4) var(--s-3) var(--s-4);
}

/* Nessun blocco si stringe. In una colonna flex i figli si comprimono PRIMA
 * di far comparire la barra di scorrimento: quando lo zoom fa crescere il
 * testo oltre l'altezza della finestra, le voci venivano schiacciate e il
 * testo tagliato a metà invece di far scorrere l'elenco. */
.colonna > * {
  flex: none;
}

.filtro {
  flex: 0 0 auto;
  padding: var(--s-3);
  border-top: 1px solid var(--rule);
}

.filtro__campo {
  width: 100%;
  box-sizing: border-box;
  padding: var(--s-2) var(--s-3);
  border: 1px solid var(--rule);
  border-radius: var(--r-2, 6px);
  background: var(--surface);
  color: var(--ink);
  font: inherit;
  font-size: var(--t-sm);
}

.filtro__campo:focus {
  outline: none;
  border-color: var(--accent, #6fa3de);
}

/* Le voci grandi: sono destinazioni intere (l'altra finestra, la griglia con
 * tutte le board), non pagine fra le altre. */
.destinazioni {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: var(--s-4);
}

.grande {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  padding: var(--s-3);
  border: 0;
  border-radius: var(--radius-lg);
  background: transparent;
  color: var(--ink);
  font: inherit;
  font-size: var(--t-md);
  font-weight: 700;
  letter-spacing: -0.01em;
  text-align: left;
  cursor: pointer;
}

.grande:hover,
.grande.router-link-exact-active {
  background: var(--surface);
}

.grande svg {
  flex: none;
  color: var(--ink-muted);
}

.grande.router-link-exact-active svg {
  color: var(--ink);
}

.voce {
  display: flex;
  align-items: center;
  gap: var(--s-2);
  /* Il tasto destro, in WebKit, seleziona la parola sotto il cursore prima di
     aprire il menu: il nome della board restava evidenziato dietro al
     pannello. Qui non c'è testo da copiare — è un elenco di destinazioni — e
     l'indirizzo si prende dal menu stesso. */
  user-select: none;
  -webkit-user-select: none;
  overflow: hidden;
  padding: var(--s-2) var(--s-3);
  border-radius: var(--radius-lg);
  font-size: var(--t-sm);
  color: var(--ink-muted);
  white-space: nowrap;
}

/* Il nome si tronca, il lucchetto no: è largo undici pixel e sparire per far
   entrare due lettere in più sarebbe il contrario di quello che serve. */
.voce__nome {
  overflow: hidden;
  text-overflow: ellipsis;
}

.voce__lucchetto {
  flex: none;
  opacity: 0.65;
}

.voce:hover {
  background: var(--surface);
  color: var(--ink);
}

/* La destinazione corrente, piena come la scheda attiva che c'era in home. */
.voce.router-link-exact-active,
.voce--attiva {
  background: var(--ink);
  color: var(--bg);
}

.voce.router-link-exact-active:hover,
.voce--attiva:hover {
  background: var(--ink);
  color: var(--bg);
}

/* La voce che riceverà il rilascio. Vince sulla voce attiva — sono due cose
 * diverse (dove sono / dove sto per lasciare) e in quel momento conta la
 * seconda — quindi la regola sta dopo e alza la specificità con :where(),
 * che non ne aggiunge di suo. */
.voce--mira,
.voce--mira:hover,
:where(.voce.router-link-exact-active, .voce--attiva).voce--mira {
  background: var(--accent);
  color: var(--accent-contrast);
  box-shadow: inset 0 0 0 2px var(--accent-hover);
}

/* Senza più una voce sopra, la distanza dal feed la dà già il suo
 * margin-bottom: un margine anche qui la raddoppierebbe. */
.testata {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--s-2) var(--s-3) var(--s-1);
}

.piu {
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: var(--radius);
  background: transparent;
  color: var(--ink-muted);
  cursor: pointer;
}

.piu:hover {
  background: var(--surface);
  color: var(--ink);
}

/* Su uno schermo stretto (browser da telefono: l'app ha una finestra ben più
 * larga) la colonna toglierebbe metà pagina: si rinuncia, il menu in alto
 * copre le stesse destinazioni. */
@media (max-width: 640px) {
  .fianco {
    display: none;
  }
}
</style>
